import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Truck, ShieldCheck, RotateCcw, Headphones } from 'lucide-react';
import { PRODUCTS, FAQS, CATEGORIES } from '../data/mock';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export default function Home() {
  const featured = PRODUCTS[PRODUCTS.length - 1]; // Smart Document Scanner
  const grid = PRODUCTS.slice(0, 8);

  return (
    <div>
      {/* Hero */}
      <section className="relative">
        <div
          className="relative h-[520px] md:h-[600px] bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?q=80&w=2672&auto=format&fit=crop')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/60 via-slate-900/35 to-slate-900/10" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
            <div className="max-w-xl text-white">
              <h1 className="font-serif text-5xl md:text-6xl font-semibold leading-tight tracking-tight">
                Office Essentials Redefined
              </h1>
              <p className="mt-5 text-lg text-white/90">
                Quality office supplies for productivity and organization.
              </p>
              <div className="mt-8">
                <Button
                  asChild
                  size="lg"
                  className="bg-[#5d728c] hover:bg-[#4d6278] text-white px-8 rounded-full"
                >
                  <Link to="/shop-all">
                    Shop Office Supplies
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Truck, label: 'Free USPS Shipping' },
            { icon: ShieldCheck, label: 'Business Trusted' },
            { icon: RotateCcw, label: '30-Day Returns' },
            { icon: Headphones, label: 'Dedicated Support' },
          ].map((v) => (
            <div key={v.label} className="flex items-center gap-3 justify-center md:justify-start">
              <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center text-[#5d728c]">
                <v.icon className="w-5 h-5" />
              </div>
              <span className="text-sm md:text-base text-slate-700 font-medium">{v.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Supply Your Success */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?q=80&w=2672&auto=format&fit=crop"
              alt="Supply your success"
              className="w-full h-[420px] object-cover rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-slate-900 tracking-tight">
              Supply Your Success
            </h2>
            <p className="mt-4 text-slate-600 text-lg">
              Premium office supplies that keep your workspace running smoothly.
            </p>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                'Premium quality materials',
                'Durable and long-lasting',
                'Professional grade',
                'Affordable pricing',
                'Wide selection',
                'Business trusted',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-[#5d728c]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Button asChild className="bg-[#5d728c] hover:bg-[#4d6278] text-white rounded-full px-7">
                <Link to="/shop-all">
                  Shop Office Supplies <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Product */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <p className="text-sm uppercase tracking-widest text-[#5d728c] font-semibold">Featured</p>
            <h2 className="mt-2 font-serif text-4xl md:text-5xl font-semibold text-slate-900">
              {featured.name}
            </h2>
            <p className="mt-3 text-2xl font-semibold text-slate-800">${featured.price.toFixed(2)}</p>
            <p className="mt-4 text-slate-600 leading-relaxed">{featured.description}</p>
            <div className="mt-8 flex gap-3">
              <Button asChild className="bg-[#5d728c] hover:bg-[#4d6278] text-white rounded-full px-7">
                <Link to={`/show/${featured.id}`}>Buy Now</Link>
              </Button>
              <Button asChild variant="outline" className="border-slate-300 rounded-full px-7">
                <Link to="/shop-all">Browse More</Link>
              </Button>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="bg-slate-50 rounded-xl p-6 shadow-md">
              <img
                src={featured.image}
                alt={featured.name}
                className="w-full h-[420px] object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900">Shop by Category</h2>
            <p className="text-slate-600 mt-2">Find exactly what your workspace needs.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/shop-all?cat=${c.slug}`}
                className="bg-white rounded-lg p-5 text-center border border-slate-200 hover:border-[#5d728c] hover:shadow-md transition-all"
              >
                <span className="text-sm font-medium text-slate-700">{c.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900">Bestsellers</h2>
              <p className="text-slate-600 mt-1">Hand-picked favorites our customers love.</p>
            </div>
            <Link to="/shop-all" className="text-[#5d728c] hover:text-[#4d6278] font-medium hidden sm:inline-flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {grid.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 text-center mb-10">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="bg-white rounded-lg border border-slate-200 px-6">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b last:border-b-0 border-slate-200">
                <AccordionTrigger className="text-left text-slate-800 hover:text-[#5d728c] font-medium">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
